import { Ref } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import DropdownMenu, { DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { Inline } from '@atlaskit/primitives';

import { FilterOption, MIGRATION_PHASES, MigrationPhase } from '../../types';
import { getMigrationPhaseDisplayText } from '../../utils';
import { messages } from '../messages';

import { MigrationPhaseFilterDropdownItem } from './migration-phase-filter-dropdown-item/migration-phase-filter-dropdown-item';

export const MigrationPhaseFilterDropdown = ({
	selectedPhase,
	onPhaseChange,
	sendMigrationPhaseDropdownClickUIEvent,
	sendMigrationPhaseDropdownItemClickUIEvent,
}: {
	selectedPhase: FilterOption<MigrationPhase> | null;
	onPhaseChange: (option: FilterOption<MigrationPhase> | null) => void;
	sendMigrationPhaseDropdownClickUIEvent: () => void;
	sendMigrationPhaseDropdownItemClickUIEvent: (selectedMigrationPhase: MigrationPhase) => void;
}) => {
	const { formatMessage } = useIntl();

	const phaseOptions: FilterOption<MigrationPhase>[] = Object.values(MIGRATION_PHASES).map(
		(phase) => {
			const phaseMessage = getMigrationPhaseDisplayText(phase);
			const phaseText = formatMessage(phaseMessage);
			return {
				label: phaseText,
				value: phase,
			};
		},
	);

	return (
		<DropdownMenu
			// This is atlaskit's recommended pattern for custom trigger components
			// eslint-disable-next-line react/no-unstable-nested-components
			trigger={({ triggerRef, ...triggerProps }) => (
				<Button
					ref={triggerRef as Ref<HTMLButtonElement>}
					{...triggerProps}
					testId="phase-filter-button"
					onClick={(e) => {
						sendMigrationPhaseDropdownClickUIEvent();
						triggerProps.onClick?.(e);
					}}
					isSelected={!!selectedPhase}
				>
					<Inline space="space.100" alignInline="center" alignBlock="center">
						{selectedPhase?.label || (
							<FormattedMessage {...messages.migrationTicketsFiltersPhasePlaceholder} />
						)}
						<ChevronDownIcon label="" size="small" testId="dropdown-icon" />
					</Inline>
				</Button>
			)}
			spacing="compact"
			shouldRenderToParent
		>
			<DropdownItemGroup>
				{phaseOptions.map((option) => (
					<MigrationPhaseFilterDropdownItem
						key={option.value}
						option={option}
						isSelected={selectedPhase?.value === option.value}
						onPhaseChange={onPhaseChange}
						sendMigrationPhaseDropdownItemClickUIEvent={sendMigrationPhaseDropdownItemClickUIEvent}
					/>
				))}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
